<template>
  <div>
    <b-container class="wide">

      <!-- Go back -->
      <b-row class="no-margin-left ml-3 mt-5">
        <b-button
          variant="outline-primary"
          @click="goToBookingCalendar"
        >
          <b-icon icon="arrow-left" class="mr-2" />
          {{ $t('links.admin.go_back') }}
        </b-button>
      </b-row>

      <!-- Content -->
      <b-row class="no-margin-left mt-4">
        <!-- Left column -->
        <b-col cols="12" md="6" lg="5">
          <div :class="{ 'checkout-form-locked': stripeActive }">
          <div class="mb-4">
            <h4 class="font-weight-bold d-flex align-items-center">
              <b-icon icon="credit-card" class="text-success mr-2" />
              {{ $t('pages.new_booking.checkout_title') }}
            </h4>
            <p class="text-muted">
              {{ $t('pages.new_booking.checkout_description') }}
            </p>
            <div v-if="!isBookingExtension && !isBookingPaymentRequest && !confirmCalled">
              <p class="mb-3" v-if="!payAsCustomer && !showLoginForm">
                {{ $t('auth.already_a_customer') }}
                <b-link href="#" @click="showLoginForm = true" class="ml-1">
                  {{ $t('auth.sign_in') }}
                </b-link>
              </p>
              <p class="mb-3" v-else-if="!payAsCustomer && showLoginForm">
                <b-link href="#" @click="showLoginForm = false" class="ml-1">
                  {{ $t('auth.back_to_anon_form') }}
                </b-link>
              </p>
            </div>
          </div>

          <!-- Customer form (anonymous only) -->
          <b-form v-if="!payAsCustomer && !showLoginForm" @submit.prevent="submitCustomerForm(onlyOffline)">
            <b-form-group
              :label="$t('form.customer.name')"
              :invalid-feedback="invalidName"
              :state="stateName"
            >
              <b-form-input
                :disabled="confirmCalled"
                v-model="customerForm.name"
                :state="stateNameInput"
                :placeholder="$t('form.customer.name')"
              />
            </b-form-group>

            <b-form-group
              :label="$t('form.customer.email')"
              :invalid-feedback="invalidEmail"
              :state="stateEmail"
            >
              <b-form-input
                :disabled="confirmCalled"
                v-model="customerForm.email"
                :state="stateEmailInput"
                :placeholder="$t('form.customer.email')"
              />
            </b-form-group>

            <b-form-group
              :label="$t('form.customer.phone')"
              :invalid-feedback="invalidPhone"
              :state="statePhone"
            >
              <vue-phone-number-input
                :disabled="confirmCalled"
                v-model="customerForm.phone"
                @update="updatePhone"
                :error="!isPhoneValid || $v.customerForm.phone.$error"
                :only-countries="phoneAvailableCountries"
                :translations="phoneTextTranslation"
                :default-country-code="phoneDefaultCountryCode"
              />
            </b-form-group>

            <b-form-group
              v-if="extraFieldVisible('address')"
              :label="$t('form.customer.address')"
              :invalid-feedback="invalidAddress"
              :state="stateAddress"
            >
              <b-form-input
                :disabled="confirmCalled"
                v-model="customerForm.address"
                :state="stateAddressInput"
                :placeholder="$t('form.customer.address')"
              />
            </b-form-group>

            <b-form-group
              v-if="extraFieldVisible('postalCode')"
              :label="$t('form.customer.postal_code')"
              :invalid-feedback="invalidPostalCode"
              :state="statePostalCode"
            >
              <b-form-input
                :disabled="confirmCalled"
                v-model="customerForm.postalCode"
                :state="statePostalCodeInput"
                :placeholder="$t('form.customer.postal_code')"
              />
            </b-form-group>

            <b-form-group
              v-if="extraFieldVisible('city')"
              :label="$t('form.customer.city')"
              :invalid-feedback="invalidCity"
              :state="stateCity"
            >
              <b-form-input
                :disabled="confirmCalled"
                v-model="customerForm.city"
                :state="stateCityInput"
                :placeholder="$t('form.customer.city')"
              />
            </b-form-group>

            <b-form-group
              v-if="extraFieldVisible('bankRegNumber')"
              :label="bankRegNumberLabel"
              :invalid-feedback="invalidBankRegNumber"
              :state="stateBankRegNumber"
            >
              <b-form-input
                :disabled="confirmCalled"
                v-model="customerForm.bankRegNumber"
                :state="stateBankRegNumberInput"
                :placeholder="bankRegNumberLabel"
              />
            </b-form-group>

            <b-form-group
              v-if="extraFieldVisible('bankAccount')"
              :label="$t('form.customer.bank_account')"
              :invalid-feedback="invalidBankAccount"
              :state="stateBankAccount"
            >
              <b-form-input
                :disabled="confirmCalled"
                v-model="customerForm.bankAccount"
                :state="stateBankAccountInput"
                :placeholder="$t('form.customer.bank_account')"
              />
            </b-form-group>

            <b-form-group
              :state="statePaymentTerms"
              :invalid-feedback="invalidPaymentTerms"
            >
              <b-form-checkbox
                v-model="paymentTermsAgreed"
                name="paymentTerms"
                id="paymentTerms"
                class="d-flex align-items-center"
              >
                {{ $t('form.booking_requests.payment_terms_agree_text') }}
                <router-link
                  v-if="!embedded"
                  :to="{ name: 'market-terms', params: { marketId: $route.params.marketId || market.marketId } }"
                  class="ml-1"
                  target="_blank"
                  rel="noopener"
                >
                  {{ $t('form.market.settings.payment_terms') }}
                </router-link>
                <b-link v-else href="#" class="ml-1" @click.prevent="$bvModal.show('embed-terms-modal')">
                  {{ $t('form.market.settings.payment_terms') }}
                </b-link>
              </b-form-checkbox>
            </b-form-group>

            <template v-if="hybridPayments">
              <b-button
                type="button"
                :disabled="loading"
                variant="success"
                size="lg"
                class="mt-3 w-100"
                @click="submitCustomerForm(false)"
              >
                <b-spinner v-if="loading && offlineChosen === false" small class="mr-2" />
                <b-icon v-else icon="shield-check" class="mr-2" />
                {{ $t('pages.new_booking.button_pay_now') }}
              </b-button>
              <b-button
                type="button"
                :disabled="loading"
                variant="outline-success"
                size="lg"
                class="mt-2 w-100"
                @click="submitCustomerForm(true)"
              >
                <b-spinner v-if="loading && offlineChosen === true" small class="mr-2" />
                <b-icon v-else icon="shop" class="mr-2" />
                {{ $t('pages.new_booking.button_pay_offline') }}
              </b-button>
            </template>
            <b-button
              v-else
              type="submit"
              :disabled="loading"
              variant="success"
              size="lg"
              class="mt-3 w-100"
            >
              <b-spinner v-if="loading" small class="mr-2" />
                <b-icon v-else icon="shield-check" class="mr-2" />
              {{ checkoutButtonLabel }}
            </b-button>
          </b-form>

          <!-- Login form (anonymous only) -->
          <b-form v-else-if="!payAsCustomer && showLoginForm" @submit.prevent="authUser">
            <b-form-group
              id="username-group"
              :label="$t('auth.email')"
              label-for="username"
              :invalid-feedback="invalidUsername"
              :state="stateUsername"
            >
              <b-form-input
                :disabled="confirmCalled"
                id="username"
                v-model="loginForm.username"
                type="text"
                :state="stateUsernameInput"
                :placeholder="$t('auth.placeholder_login')"
              ></b-form-input>
            </b-form-group>
            <b-form-group
              id="password-group"
              :label="$t('auth.password')"
              label-for="password"
              :invalid-feedback="invalidPassword"
              :state="statePassword"
            >
              <b-form-input
                :disabled="confirmCalled"
                id="password"
                v-model="loginForm.password"
                type="password"
                @blur="invalidCredentials = null"
                :state="statePasswordInput"
                :placeholder="$t('auth.placeholder_password')"
              ></b-form-input>
            </b-form-group>

            <b-form-group
              :state="statePaymentTerms"
              :invalid-feedback="invalidPaymentTerms"
            >
              <b-form-checkbox
                v-model="paymentTermsAgreed"
                name="paymentTerms"
                id="paymentTerms"
                class="d-flex align-items-center"
              >
                {{ $t('form.booking_requests.payment_terms_agree_text') }}
                <router-link
                  v-if="!embedded"
                  :to="{ name: 'market-terms', params: { marketId: $route.params.marketId || market.marketId } }"
                  class="ml-1"
                  target="_blank"
                  rel="noopener"
                >
                  {{ $t('form.market.settings.payment_terms') }}
                </router-link>
                <b-link v-else href="#" class="ml-1" @click.prevent="$bvModal.show('embed-terms-modal')">
                  {{ $t('form.market.settings.payment_terms') }}
                </b-link>
              </b-form-checkbox>
            </b-form-group>

            <div class="form-actions">
              <b-button
                :disabled="loading"
                variant="success"
                size="lg"
                class="w-100"
                type="submit"
              >
                <b-spinner v-if="loading" small class="mr-2" />
                <b-icon v-else icon="shield-check" class="mr-2" />
                {{ loading ? $t('pages.new_booking.opening_payment') : $t('pages.new_booking.button_confirm_checkout') }}
              </b-button>
            </div>
          </b-form>

          <!-- Authenticated: show only terms and button -->
          <div v-else class="mt-4">
            <!-- Terms form (customer) -->
            <b-form @submit.prevent="submitCustomerForm(onlyOffline)">

              <p v-if="anyExtraFieldVisible" class="text-muted">
                {{ $t('pages.new_booking.missing_profile_fields_hint') }}
              </p>

              <b-form-group
                v-if="extraFieldVisible('address')"
                :label="$t('form.customer.address')"
                :invalid-feedback="invalidAddress"
                :state="stateAddress"
              >
                <b-form-input
                  :disabled="confirmCalled"
                  v-model="customerForm.address"
                  :state="stateAddressInput"
                  :placeholder="$t('form.customer.address')"
                />
              </b-form-group>

              <b-form-group
                v-if="extraFieldVisible('postalCode')"
                :label="$t('form.customer.postal_code')"
                :invalid-feedback="invalidPostalCode"
                :state="statePostalCode"
              >
                <b-form-input
                  :disabled="confirmCalled"
                  v-model="customerForm.postalCode"
                  :state="statePostalCodeInput"
                  :placeholder="$t('form.customer.postal_code')"
                />
              </b-form-group>

              <b-form-group
                v-if="extraFieldVisible('city')"
                :label="$t('form.customer.city')"
                :invalid-feedback="invalidCity"
                :state="stateCity"
              >
                <b-form-input
                  :disabled="confirmCalled"
                  v-model="customerForm.city"
                  :state="stateCityInput"
                  :placeholder="$t('form.customer.city')"
                />
              </b-form-group>

              <b-form-group
                v-if="extraFieldVisible('bankRegNumber')"
                :label="bankRegNumberLabel"
                :invalid-feedback="invalidBankRegNumber"
                :state="stateBankRegNumber"
              >
                <b-form-input
                  :disabled="confirmCalled"
                  v-model="customerForm.bankRegNumber"
                  :state="stateBankRegNumberInput"
                  :placeholder="bankRegNumberLabel"
                />
              </b-form-group>

              <b-form-group
                v-if="extraFieldVisible('bankAccount')"
                :label="$t('form.customer.bank_account')"
                :invalid-feedback="invalidBankAccount"
                :state="stateBankAccount"
              >
                <b-form-input
                  :disabled="confirmCalled"
                  v-model="customerForm.bankAccount"
                  :state="stateBankAccountInput"
                  :placeholder="$t('form.customer.bank_account')"
                />
              </b-form-group>

              <b-form-group
                :state="statePaymentTerms"
                :invalid-feedback="invalidPaymentTerms"
              >
                <b-form-checkbox
                  v-model="paymentTermsAgreed"
                  name="paymentTerms"
                  id="paymentTerms"
                  class="d-flex align-items-center"
                >
                  {{ $t('form.booking_requests.payment_terms_agree_text') }}
                  <router-link
                    v-if="!embedded"
                    :to="{ name: 'market-terms', params: { marketId: $route.params.marketId || market.marketId } }"
                    class="ml-1"
                    target="_blank"
                    rel="noopener"
                  >
                    {{ $t('form.market.settings.payment_terms') }}
                  </router-link>
                  <b-link v-else href="#" class="ml-1" @click.prevent="$bvModal.show('embed-terms-modal')">
                    {{ $t('form.market.settings.payment_terms') }}
                  </b-link>
                </b-form-checkbox>
              </b-form-group>
              <template v-if="hybridPayments">
                <b-button
                  :disabled="loading || confirmCalled"
                  variant="success"
                  size="lg"
                  class="w-100"
                  type="button"
                  @click="submitCustomerForm(false)"
                >
                  <b-spinner v-if="loading && offlineChosen === false" small class="mr-2" />
                  <b-icon v-else icon="shield-check" class="mr-2" />
                  {{ $t('pages.new_booking.button_pay_now') }}
                </b-button>
                <b-button
                  :disabled="loading || confirmCalled"
                  variant="outline-success"
                  size="lg"
                  class="w-100 mt-2"
                  type="button"
                  @click="submitCustomerForm(true)"
                >
                  <b-spinner v-if="loading && offlineChosen === true" small class="mr-2" />
                  <b-icon v-else icon="shop" class="mr-2" />
                  {{ $t('pages.new_booking.button_pay_offline') }}
                </b-button>
              </template>
              <b-button
                v-else
                :disabled="loading || confirmCalled"
                variant="success"
                size="lg"
                class="w-100"
                type="submit"
              >
                <b-spinner v-if="loading" small class="mr-2" />
                <b-icon v-else icon="shield-check" class="mr-2" />
                {{ checkoutButtonLabel }}
              </b-button>
            </b-form>
          </div>
          </div>

          <transition name="stripe-reveal">
            <div v-if="stripeActive" class="stripe-pay-panel mt-4">
              <div class="stripe-pay-panel-head">
                <b-icon icon="lock-fill" class="text-success mr-2" />
                {{ $t('pages.new_booking.pay_securely') }}
              </div>
              <div id="stripe-embedded-checkout"></div>
              <b-button variant="link" class="stripe-pay-back mt-2" @click="cancelStripeCheckout">
                <b-icon icon="arrow-left" class="mr-1" />
                {{ $t('modal.general.cancel_button') }}
              </b-button>
            </div>
          </transition>
        </b-col>

        <!-- Booking Summary Card -->
        <b-col cols="12" md="6" lg="5" class="mt-4">
          <booking-summary-card
            :bookings="bookingsInfo"
            :newBooking="null"
            :market="market"
            :isCheckoutPage="true"
            :isCustomerView="payAsCustomer"
            :isBookingExtension="isBookingExtension"
            @deleteBooking="handleDeleteBooking"
          />
        </b-col>
      </b-row>
    </b-container>

    <!-- Embed: show terms in-place so the iframe is never navigated away -->
    <b-modal
      id="embed-terms-modal"
      :title="$t('pages.bookings.market_terms')"
      hide-footer
      scrollable
    >
      <div v-html="market.paymentTerms" />
    </b-modal>
  </div>
</template>

<script>
import Formatting from "@base/mixins/Formatting.vue";
import TableUtils from "@base/mixins/TableUtils.vue";
import BankAccountFields from "@base/mixins/BankAccountFields.vue";
import BookingSummaryCard from "@base/components/booking/BookingSummaryCard.vue";
import { email, maxLength, minLength, numeric, required } from "vuelidate/lib/validators";
import {
  BOOKING_FIELD_HIDDEN,
  BOOKING_FIELD_REQUIRED,
  COUNTRIES_ARRAY_ONLY,
  GATEWAY_ELAVON,
  GATEWAY_STRIPE,
  GB_COUNTRY,
  PHONE_COUNTRY_CODE_MAPPING,
  PHONE_NUMBER_INPUT_TRANSLATIONS,
  STRIPE_PUBLISHABLE_KEY,
  USER_SECTION_PATH,
} from "@base/configs";
import { isEmbedded } from "@base/utils";
import loadStripeJs from "@base/stripe-js-loader";
import { ibanValidator, noQuotesValidator } from "@base/customValidators";
import { clearScoped, readScoped, writeScoped } from "@base/storage";
import {
  customerRequestInterceptor,
  customerResponseFulfilledInterceptor,
  customerResponseRejectedInterceptor
} from "@base/store";
import {bookingRequest} from "@anonymous/services";

const defaultCustomerForm = {
  name: '',
  email: '',
  phone: null,
  address: '',
  postalCode: '',
  city: '',
  bankRegNumber: '',
  bankAccount: '',
};

const EXTRA_ADDRESS_FIELDS = ['address', 'postalCode', 'city'];
const EXTRA_BANK_FIELDS = ['bankRegNumber', 'bankAccount'];

const defaultLoginForm = {
  username: '',
  password: '',
};

// A redirect method finalises via webhook, so confirm can return "pending" - poll first.
const CONFIRM_MAX_ATTEMPTS = 10;
const CONFIRM_RETRY_DELAY_MS = 2500;

// A MobilePay app-switch reloads the SPA - the anon form must survive for the returning confirm
const ANON_CHECKOUT_FORM_KEY = 'anonBookingCheckoutForm';

export default {
  name: 'BookingCheckoutComponent',

  components: { BookingSummaryCard },
  mixins: [ Formatting, TableUtils, BankAccountFields ],

  data () {
    return {
      loading: false,
      offlineChosen: null,
      customerForm: { ...defaultCustomerForm },
      loginForm: { ...defaultLoginForm, username: this.customerEmail },
      phoneNumberData: null,
      paymentTermsAgreed: null,
      confirmCalled: false,
      showLoginForm: this.isBookingExtension || this.isBookingPaymentRequest,
      invalidCredentials: null,
      phoneAvailableCountries: COUNTRIES_ARRAY_ONLY,
      handledMap: new Map(),
      txnId: null,
      embeddedCustomer: false,
      stripeCheckout: null,
      stripeActive: false,
      profileLoaded: false,
      profileFilled: {},
    }
  },

  props: {
    bookingsInfo: {
      type: Array,
      required: true
    },
    market: {
      type: Object,
      required: true
    },
    isCustomerBooking: {
      type: Boolean,
      default: false
    },
    isBookingExtension: {
      type: Boolean,
      default: false
    },
    isBookingPaymentRequest: {
      type: Boolean,
      default: false
    },
    customerEmail: {
      type: String,
      default: ''
    }
  },

  validations () {
    if (!this.payAsCustomer && !this.showLoginForm) {
      return {
        customerForm: {
          phone: { required },
          name: { required, noQuotesValidator },
          email: { email, required },
          ...this.extraFieldsValidations,
        },
        paymentTermsAgreed: {
          checked: value => value === true,
        }
      }
    } else if (!this.payAsCustomer && this.showLoginForm) {
      return {
        loginForm: {
          username: {
            required,
          },
          password: {
            required
          },
        },
        paymentTermsAgreed: {
          checked: value => value === true,
        }
      }
    } else {
      return {
        customerForm: {
          ...this.extraFieldsValidations,
        },
        paymentTermsAgreed: {
          checked: value => value === true,
        }
      }
    }
  },

  computed: {
    embedded () {
      return isEmbedded()
    },
    // After an existing customer logs in inside the embed iframe we pay as a customer.
    payAsCustomer () {
      return this.isCustomerBooking || this.embeddedCustomer
    },
    extraFieldsActive () {
      return !this.isBookingPaymentRequest
    },
    addressFieldMode () {
      return (this.market && this.market.bookingAddressFieldMode) || BOOKING_FIELD_HIDDEN
    },
    bankFieldMode () {
      return (this.market && this.market.bookingBankFieldMode) || BOOKING_FIELD_HIDDEN
    },
    bankCountryCode () {
      return this.market.supportCountry && this.market.supportCountry.code
    },
    bankFormatCode () {
      return this.market.bankFileFormat
    },
    bankRegNumberLabel () {
      return this.bankCountryCode === GB_COUNTRY
        ? this.$t('form.customer.sort_code')
        : this.$t('form.customer.bank_reg_number')
    },
    anyExtraFieldVisible () {
      return [...EXTRA_ADDRESS_FIELDS, ...EXTRA_BANK_FIELDS].some(field => this.extraFieldVisible(field))
    },
    extraFieldsValidations () {
      const validator = {}
      const addressRequired = this.addressFieldMode === BOOKING_FIELD_REQUIRED
      const bankRequired = this.bankFieldMode === BOOKING_FIELD_REQUIRED
      for (const field of EXTRA_ADDRESS_FIELDS) {
        if (this.extraFieldVisible(field)) {
          validator[field] = addressRequired ? { required } : {}
        }
      }
      if (this.extraFieldVisible('bankRegNumber')) {
        validator.bankRegNumber = {
          numeric,
          minLength: minLength(this.bankRegNumberLengthFor(this.bankCountryCode)),
          maxLength: maxLength(this.bankRegNumberLengthFor(this.bankCountryCode)),
          ...(bankRequired && { required }),
        }
      }
      if (this.extraFieldVisible('bankAccount')) {
        validator.bankAccount = {
          maxLength: maxLength(this.bankAccountLengthFor(this.bankCountryCode, this.bankFormatCode)),
          ...(this.accountIsIban(this.bankCountryCode, this.bankFormatCode) && { ibanValidator }),
          ...(bankRequired && { required }),
        }
      }
      return validator
    },
    onlineAvailable () {
      return this.market.allowOnlinePayments && this.market.gatewayApiValid
    },
    onlyOffline () {
      return this.market.allowOfflinePayments && !this.onlineAvailable && !this.isBookingPaymentRequest
    },
    hybridPayments () {
      return this.onlineAvailable && this.market.allowOfflinePayments && !this.isBookingPaymentRequest
    },
    checkoutButtonLabel () {
      if (this.loading) {
        return this.onlyOffline
          ? this.$t('pages.new_booking.confirming_booking')
          : this.$t('pages.new_booking.opening_payment')
      }
      return this.onlyOffline
        ? this.$t('pages.new_booking.button_pay_offline')
        : this.$t('pages.new_booking.button_confirm_checkout')
    },
    loginPayload () {
      return {
        username: this.loginForm.username,
        password: this.loginForm.password,
        market_id: this.market.marketId
      }
    },

    updatedReservedBookingRequestPayload () {
      return {
        bookingIds: this.bookingsInfo.map(obj => obj.bookingRequest),
        cartToken: localStorage.getItem(this.cartTokenKey) || null,
      }
    },

    paymentReturnUrl () {
      const url = new URL(window.location.href)
      url.searchParams.set('stripe_return', '1')
      return url.href
    },
    initPaymentPayload () {
      let payload = {
        bookingIds: this.bookingsInfo.map(obj => obj.bookingRequest),
        cartToken: localStorage.getItem(this.cartTokenKey) || null,
        returnUrl: this.paymentReturnUrl,
        ...(isEmbedded() && { embedded: true }),
      }
      if (!this.payAsCustomer) {
        payload = {
          ...payload,
          customerName: this.customerForm.name,
          customerEmail: this.customerForm.email,
          customerPhone: this.phoneNumberData?.formattedNumber || '',
          ...(this.extraFieldsActive && this.addressFieldMode !== BOOKING_FIELD_HIDDEN && {
            customerAddress: this.customerForm.address || '',
            customerPostalCode: this.customerForm.postalCode || '',
            customerCity: this.customerForm.city || '',
          }),
          ...(this.extraFieldsActive && this.bankFieldMode !== BOOKING_FIELD_HIDDEN && {
            customerBankRegNumber: this.customerForm.bankRegNumber || '',
            customerBankAccount: this.customerForm.bankAccount || '',
          }),
        }
      }
      return payload
    },
    confirmPaymentPayload () {
      return {
        ...this.initPaymentPayload,
        paymentId: localStorage.getItem(this.paymentKey) || null,
      }
    },

    paymentKey() {
      return this.isBookingExtension
        ? 'extendPaymentId'
        : this.isBookingPaymentRequest
          ? 'paymentRequestPaymentId'
          : 'paymentId'
    },
    cartTokenKey() {
      return this.isBookingExtension
        ? 'extendCartToken'
        : this.isBookingPaymentRequest
          ? 'paymentRequestCartToken'
          : 'cartToken'
    },
    bookingsInfoKey() {
      return this.isBookingExtension
        ? 'extendBookingsInfo'
        : this.isBookingPaymentRequest
          ? 'paymentRequestBookingsInfo'
          : 'bookingsInfo'
    },

    phoneDefaultCountryCode() {
        return this.market.supportCountry ? this.market.supportCountry.code : PHONE_COUNTRY_CODE_MAPPING[this.$i18n.locale.toUpperCase()]
      },
    phoneTextTranslation() {
      return PHONE_NUMBER_INPUT_TRANSLATIONS[this.$i18n.locale.toUpperCase()]
    },
    isPhoneValid() {
      if (!this.phoneNumberData)
        return true
      return this.phoneNumberData && this.customerForm.phone && this.phoneNumberData.isValid
    },
    statePhone() {
      return (!this.isPhoneValid || this.$v.customerForm.phone.$error) ? false : null
    },
    invalidPhone() {
      if (this.$v.customerForm.phone.$error) {
        if (!this.$v.customerForm.phone.required)
          return this.$t('validation.required', {field: this.$t('form.market.phone')})
      }
      return null
    },
    stateNameInput() {
      return this.$v.customerForm.name.$error ? false : null
    },
    stateName() {
      return !(this.$v.customerForm.name.$error)
    },
    invalidName() {
      if (this.$v.customerForm.name.$error) {
        if (!this.$v.customerForm.name.required)
          return this.$t('validation.required', {field: this.$t('form.customer.name')})
        else if (!this.$v.customerForm.name.noQuotesValidator)
          return this.$t('validation.quotes_not_allowed', {
            field: this.$t('form.customer.name'),
          })
      }
      return null
    },
    statePaymentTerms() {
      return !(this.$v.paymentTermsAgreed.$error)
    },
    invalidPaymentTerms() {
      if (this.$v.paymentTermsAgreed.$error) {
        if (!this.$v.paymentTermsAgreed.checked)
          return this.$t('validation.required', {field: this.$t('form.market.settings.payment_terms')})
      }
      return null
    },
    stateEmailInput() {
      return this.$v.customerForm.email.$error ? false : null
    },
    stateEmail() {
      return !(this.$v.customerForm.email.$error)
    },
    invalidEmail() {
      if (this.$v.customerForm.email.$error) {
        if (!this.$v.customerForm.email.email)
          return this.$t('validation.email')
        else if (!this.$v.customerForm.email.required)
          return this.$t('validation.required', {field: this.$t('form.customer.email')})
      }
      return null
    },
    stateUsernameInput () {
      return (this.$v.loginForm.username.$error || this.invalidCredentials !== null) ? false : null
    },
    stateUsername () {
      return !this.$v.loginForm.username.$error || this.invalidCredentials !== null
    },
    invalidUsername () {
      if (this.$v.loginForm.username.$error) {
        if (!this.$v.loginForm.username.required)
          return this.$t('validation.required', { field: this.$t('auth.field.username') })
      }
      return null
    },
    statePasswordInput () {
      return (this.$v.loginForm.password.$error || this.invalidCredentials !== null) ? false : null
    },
    statePassword () {
      return !this.$v.loginForm.password.$error || this.invalidCredentials !== null
    },
    invalidPassword () {
      if (this.$v.loginForm.password.$error) {
        if (!this.$v.loginForm.password.required)
          return this.$t('validation.required', { field: this.$t('auth.field.password') })
      }
      return null
    },
    stateAddressInput () {
      const field = this.$v.customerForm && this.$v.customerForm.address
      return field && field.$error ? false : null
    },
    stateAddress () {
      const field = this.$v.customerForm && this.$v.customerForm.address
      return !(field && field.$error)
    },
    invalidAddress () {
      const field = this.$v.customerForm && this.$v.customerForm.address
      if (field && field.$error) {
        if (!field.required)
          return this.$t('validation.required', { field: this.$t('form.customer.address') })
      }
      return null
    },
    statePostalCodeInput () {
      const field = this.$v.customerForm && this.$v.customerForm.postalCode
      return field && field.$error ? false : null
    },
    statePostalCode () {
      const field = this.$v.customerForm && this.$v.customerForm.postalCode
      return !(field && field.$error)
    },
    invalidPostalCode () {
      const field = this.$v.customerForm && this.$v.customerForm.postalCode
      if (field && field.$error) {
        if (!field.required)
          return this.$t('validation.required', { field: this.$t('form.customer.postal_code') })
      }
      return null
    },
    stateCityInput () {
      const field = this.$v.customerForm && this.$v.customerForm.city
      return field && field.$error ? false : null
    },
    stateCity () {
      const field = this.$v.customerForm && this.$v.customerForm.city
      return !(field && field.$error)
    },
    invalidCity () {
      const field = this.$v.customerForm && this.$v.customerForm.city
      if (field && field.$error) {
        if (!field.required)
          return this.$t('validation.required', { field: this.$t('form.customer.city') })
      }
      return null
    },
    stateBankRegNumberInput () {
      const field = this.$v.customerForm && this.$v.customerForm.bankRegNumber
      return field && field.$error ? false : null
    },
    stateBankRegNumber () {
      const field = this.$v.customerForm && this.$v.customerForm.bankRegNumber
      return !(field && field.$error)
    },
    invalidBankRegNumber () {
      const field = this.$v.customerForm && this.$v.customerForm.bankRegNumber
      if (field && field.$error) {
        if (field.required === false)
          return this.$t('validation.required', { field: this.bankRegNumberLabel })
        else if (!field.numeric)
          return this.$t('validation.numeric', { field: this.bankRegNumberLabel })
        else if (!field.minLength)
          return this.$t('validation.min_length', {
            field: this.bankRegNumberLabel,
            minLength: field.$params.minLength.min
          })
        else if (!field.maxLength)
          return this.$t('validation.max_length', {
            field: this.bankRegNumberLabel,
            maxLength: field.$params.maxLength.max
          })
      }
      return null
    },
    stateBankAccountInput () {
      const field = this.$v.customerForm && this.$v.customerForm.bankAccount
      return field && field.$error ? false : null
    },
    stateBankAccount () {
      const field = this.$v.customerForm && this.$v.customerForm.bankAccount
      return !(field && field.$error)
    },
    invalidBankAccount () {
      const field = this.$v.customerForm && this.$v.customerForm.bankAccount
      if (field && field.$error) {
        if (field.required === false)
          return this.$t('validation.required', { field: this.$t('form.customer.bank_account') })
        else if (!field.maxLength)
          return this.$t('validation.max_length', {
            field: this.$t('form.customer.bank_account'),
            maxLength: field.$params.maxLength.max
          })
        else if (field.ibanValidator === false)
          return this.$t('validation.iban', { field: this.$t('form.customer.bank_account') })
      }
      return null
    },
  },

  watch: {
    'loginForm.username' () {
      this.invalidCredentials = null
    },
    'loginForm.password' () {
      this.invalidCredentials = null
    },
    payAsCustomer (value) {
      if (value) {
        this.loadCustomerProfile()
      }
    },
  },

  mounted () {
    this.resumeRedirectedPayment()
    // An abandoned payment must not leave the saved form (incl. bank details) behind.
    if (!localStorage.getItem(this.paymentKey)) {
      this.clearAnonCheckoutForm()
    }
    if (this.payAsCustomer) {
      this.loadCustomerProfile()
    }
  },

  beforeDestroy () {
    this.destroyStripeCheckout()
  },

  methods: {
    goToBookingCalendar() {
      this.$emit('backToBookingCalendar')
    },

    updatePhone(phoneObj) {
      this.phoneNumberData = phoneObj
    },

    async submitCustomerForm(offline) {
      this.$v.$touch()
      if (this.$v.$invalid) return
      if (this.payAsCustomer && !(await this.saveProfileFields())) return
      await this.startPayment(offline)
    },

    extraFieldVisible (field) {
      const mode = EXTRA_ADDRESS_FIELDS.includes(field) ? this.addressFieldMode : this.bankFieldMode
      if (!this.extraFieldsActive || mode === BOOKING_FIELD_HIDDEN)
        return false
      if (field === 'bankRegNumber' && !this.usesBankRegNumber(this.bankCountryCode, this.bankFormatCode))
        return false
      if (this.payAsCustomer)
        return this.profileLoaded && !this.profileFilled[field]
      return true
    },

    async loadCustomerProfile () {
      if (!this.extraFieldsActive)
        return
      if (this.addressFieldMode === BOOKING_FIELD_HIDDEN && this.bankFieldMode === BOOKING_FIELD_HIDDEN)
        return
      try {
        const profile = await this.$store.dispatch(this.$_actionTypes.GET_CUSTOMER_PROFILE)
        this.profileFilled = {
          address: Boolean(profile.address),
          postalCode: Boolean(profile.postalCode),
          city: Boolean(profile.city),
          bankRegNumber: Boolean(profile.bankRegNumber),
          bankAccount: Boolean(profile.bankAccount),
        }
        this.profileLoaded = true
      } catch (error) {
        // A failed profile read must not block checkout - the fields simply stay hidden.
      }
    },

    async saveProfileFields () {
      const payloads = {}
      for (const field of [...EXTRA_ADDRESS_FIELDS, ...EXTRA_BANK_FIELDS]) {
        if (this.extraFieldVisible(field) && this.customerForm[field]) {
          payloads[field] = this.customerForm[field]
        }
      }
      if (!Object.keys(payloads).length)
        return true
      try {
        await this.$store.dispatch(this.$_actionTypes.UPDATE_CUSTOMER_PROFILE, payloads)
        return true
      } catch (error) {
        this.$_notifyError(error, this)
        return false
      }
    },

    async startPayment (offline) {
      this.offlineChosen = offline
      if (offline) {
        await this.bookWithoutPayment()
        return
      }
      const starters = {
        [GATEWAY_STRIPE]: () => this.startStripePayment(),
        [GATEWAY_ELAVON]: () => this.startElavonPayment(),
      }
      await (starters[this.market.gatewayProvider] || starters[GATEWAY_ELAVON])()
    },

    async bookWithoutPayment () {
      this.loading = true
      this.loader = this.$loading.show(this.$_loaderOptions)
      this.confirmCalled = true
      try {
        const action = this.payAsCustomer
          ? this.$_actionTypes.BOOKING_REQUEST_BOOK_WITHOUT_PAYMENT
          : this.$_actionTypes.BOOKING_REQUEST_BOOK_WITHOUT_PAYMENT_ANON
        await this.$store.dispatch(action, this.initPaymentPayload)
        localStorage.removeItem(this.bookingsInfoKey)
        localStorage.removeItem(this.cartTokenKey)
        localStorage.removeItem(this.paymentKey)
        this.clearAnonCheckoutForm()
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('notifications.title.success'),
          text: this.$t('notifications.text.create_booking.success_booking_confirmed')
        })
        this.$emit('paymentSuccessful')
      } catch (error) {
        if (error?.data?.customerEmail) {
          this.showLoginForm = true
          this.loginForm.username = this.customerForm.email
        }
        this.$_notifyError(error, this)
      } finally {
        this.confirmCalled = false
        this.loader.hide()
        this.loading = false
      }
    },

    persistAnonCheckoutForm () {
      if (this.payAsCustomer) return
      writeScoped(ANON_CHECKOUT_FORM_KEY, this.market.marketId, {
        ...this.customerForm,
        formattedPhone: this.phoneNumberData?.formattedNumber || '',
      })
    },

    restoreAnonCheckoutForm () {
      if (this.payAsCustomer) return
      const saved = readScoped(ANON_CHECKOUT_FORM_KEY, this.market.marketId)
      if (!saved) return
      const { formattedPhone, ...form } = saved
      this.customerForm = { ...this.customerForm, ...form }
      if (formattedPhone) {
        this.phoneNumberData = { isValid: true, formattedNumber: formattedPhone }
      }
    },

    clearAnonCheckoutForm () {
      clearScoped(ANON_CHECKOUT_FORM_KEY, this.market.marketId)
    },

    resumeRedirectedPayment() {
      // Redirect methods (MobilePay) come back here instead of firing onComplete.
      const url = new URL(window.location.href)
      if (url.searchParams.get('stripe_return') !== '1') return
      url.searchParams.delete('stripe_return')
      window.history.replaceState({}, '', url.href)
      if (!localStorage.getItem(this.paymentKey)) return
      this.restoreAnonCheckoutForm()
      this.confirmCalled = true
      this.confirmPayment()
    },

    async confirmPayment() {
      if (!this.confirmCalled) return
      const action = this.payAsCustomer
          ? this.$_actionTypes.BOOKING_REQUEST_CONFIRM_PAYMENT
          : this.$_actionTypes.BOOKING_REQUEST_CONFIRM_PAYMENT_ANON

      for (let attempt = 0; attempt < CONFIRM_MAX_ATTEMPTS; attempt += 1) {
        try {
          const data = await this.$store.dispatch(action, this.confirmPaymentPayload)
          // a redirect/async method (MobilePay) can still be processing - re-check
          if (data && data.pending) {
            await new Promise(resolve => setTimeout(resolve, CONFIRM_RETRY_DELAY_MS))
            continue
          }
          localStorage.removeItem(this.bookingsInfoKey)
          localStorage.removeItem(this.cartTokenKey)
          localStorage.removeItem(this.paymentKey)
          this.clearAnonCheckoutForm()
          this.$notify({
            group: 'app',
            type: 'success',
            title: this.$t('notifications.title.success'),
            text: this.$t('notifications.text.create_booking.success_booking_confirmed')
          })
          this.$emit("paymentSuccessful")
          this.confirmCalled = false
          return
        } catch (error) {
          this.confirmCalled = false
          // the redirect method came back declined - never mark paid, let them retry
          if (error && error.data && error.data.paymentFailed) {
            localStorage.removeItem(this.paymentKey)
            this.destroyStripeCheckout()
            this.$notify({
              group: 'app',
              type: 'error',
              title: this.$t('notifications.title.error'),
              text: this.$t('notifications.text.create_booking.error_payment_failed')
            })
            return
          }
          this.$_notifyError(error, this)
          return
        }
      }
      // still processing after all retries - the webhook will finalise it shortly
      this.confirmCalled = false
      localStorage.removeItem(this.paymentKey)
      this.$notify({
        group: 'app',
        type: 'warn',
        title: this.$t('notifications.title.warning'),
        text: this.$t('notifications.text.create_booking.info_payment_processing')
      })
    },

    async deleteBooking (bookingInfo) {
      return new Promise((resolve, reject) => {
        if (this.loading)
          return
        this.loading = true
        this.loader = this.$loading.show(this.$_loaderOptions)
        const action = this.isCustomerBooking
            ? this.$_actionTypes.BOOKING_REQUEST_DELETE
            : this.$_actionTypes.BOOKING_REQUEST_DELETE_ANON
        const payload = this.isCustomerBooking
            ? bookingInfo.bookingRequest
            : { id: bookingInfo.bookingRequest, cartToken: bookingInfo.cartToken }
        this.$store.dispatch(action, payload)
          .then(() => {
            const oldBooking = this.bookingsInfo.findIndex(
              obj => obj.bookingRequest === bookingInfo.bookingRequest
            )
            this.bookingsInfo.splice(oldBooking, 1)
            localStorage.setItem(this.bookingsInfoKey, JSON.stringify(this.bookingsInfo))
            this.$notify({
              group: 'app',
              type: 'success',
              title: this.$t('notifications.title.success'),
              text: this.$t('notifications.text.create_booking.success_booking_deleted')
            })
            resolve()
          })
          .catch((error) => {
            if (error.status === 404) {
              const oldBooking = this.bookingsInfo.findIndex(
              obj => obj.bookingRequest === bookingInfo.bookingRequest
            )
            this.bookingsInfo.splice(oldBooking, 1)
            localStorage.setItem(this.bookingsInfoKey, JSON.stringify(this.bookingsInfo))
            this.$notify({
              group: 'app',
              type: 'success',
              title: this.$t('notifications.title.success'),
              text: this.$t('notifications.text.create_booking.success_booking_deleted')
            })
              resolve()
            } else {
              this.$_notifyError(error, this)
              reject()
            }
          })
          .finally(() => {
            this.loader.hide()
            this.loading = false
          })
      })
    },

    async initUser () {
      try {
        await this.$store.dispatch(this.$_actionTypes.INIT_USER, 'CustomerToken')
      } catch (error) {
        this.$_notifyError(error.response, this)
        throw error.response
      }
    },

    async updateReservedBooking () {
        bookingRequest.interceptors.request.use(customerRequestInterceptor)
        bookingRequest.interceptors.response.use(
          customerResponseFulfilledInterceptor,
          customerResponseRejectedInterceptor
        )
      try {
        await bookingRequest({
          url: `${this.market.marketId}/reserve_update/`,
          data: this.updatedReservedBookingRequestPayload,
        })
      } catch (error) {
        this.$_notifyError(error.response, this)
        throw error.response
      }
    },

    async authUser () {
      this.$v.$touch()
      if (this.$v.$invalid)
        return
      this.invalidCredentials = null
      this.loading = true
      this.loader = this.$loading.show(this.$_loaderOptions)
      try {
        await this.$store.dispatch(this.$_actionTypes.LOGIN_USER, this.loginPayload)
        await this.initUser()
        if (isEmbedded()) {
          // Embed: finish payment in-iframe as a customer instead of loading /user.
          await this.updateReservedBooking()
          this.embeddedCustomer = true
          this.showLoginForm = false
        } else if (this.isBookingExtension || this.isBookingPaymentRequest) {
          // Clean forward slash from this.$route.path
          window.location.replace(`${USER_SECTION_PATH}${this.$route.path.replace(/^\/+/, '')}`)
        } else {
          await this.updateReservedBooking()
          window.location.replace(`${USER_SECTION_PATH}create_booking`)
        }
      } catch (error) {
        this.invalidCredentials = error?.data?.error
        if (this.invalidCredentials) {
          this.$notify({
            group: 'app',
            type: 'error',
            title: this.$t('notifications.title.error'),
            text: this.invalidCredentials
          })
        }
      } finally {
        this.loading = false
        this.loader.hide()
      }
    },

    async failBookingTx () {
      return new Promise((resolve) => {
        const action = this.payAsCustomer
            ? this.$_actionTypes.BOOKING_PAYMENT_TRANSACTION_FAIL
            : this.$_actionTypes.BOOKING_PAYMENT_TRANSACTION_FAIL_ANON
        this.$store.dispatch(action, this.txnId)
          .then(() => {
            resolve()
          })
          .catch(() => {
            resolve()
          })
      })
    },

    async cancelBookingTx () {
      return new Promise((resolve) => {
        const action = this.payAsCustomer
            ? this.$_actionTypes.BOOKING_PAYMENT_TRANSACTION_CANCEL
            : this.$_actionTypes.BOOKING_PAYMENT_TRANSACTION_CANCEL_ANON
        this.$store.dispatch(action, this.txnId)
          .then(() => {
            resolve()
          })
          .catch(() => {
            resolve()
          })
      })
    },

    async handleDeleteBooking (bookingInfo) {
      const parentBookingRequest = bookingInfo.parentBookingRequest
      await this.deleteBooking(bookingInfo)
      this.$emit('bookingDeleted', this.bookingsInfo, parentBookingRequest)
    },

    /* ============ LIGHTBOX FLOW ============ */
    async startElavonPayment () {
      this.loading = true;
      this.loader  = this.$loading.show(this.$_loaderOptions);
      this.destroyOldLightbox();
      this.handledMap.clear();
      this.txnId = null;

      try {
        /* 1. create payment-session (hppType='lightbox') */
        const data = await this.$store.dispatch(
          this.payAsCustomer
            ? this.$_actionTypes.BOOKING_REQUEST_INIT_PAYMENT
            : this.$_actionTypes.BOOKING_REQUEST_INIT_PAYMENT_ANON,
          this.initPaymentPayload
        );

        localStorage.setItem(this.paymentKey, data.paymentId);

        this.txnId = data.txId

        /* 2. ensure library is loaded */
        await this.$nextTick();
        if (typeof window.ElavonLightbox !== 'function') {
          throw new Error('Elavon Lightbox SDK not loaded');
        }

        /* 3. open lightbox */
        const MessageTypes = window.ElavonLightbox.MessageTypes;
        const lightbox = new window.ElavonLightbox({
          sessionId: data.paymentId,
          /* callback for all messages from HPP */
          messageHandler: async (msg, next) => {
            const key = `${msg.sessionId || ''}:${msg.type}`;
            if (this.handledMap.has(key)) {
              next(); // do not enter the switch when duplicated events come
              return;
            }
            this.handledMap.set(key, true);
            switch (msg.type) {
              case MessageTypes.loaded:
                this.confirmCalled = true
                break;
              case MessageTypes.closeOverlay:
                await this.cancelBookingTx();
                this.confirmCalled = false;
                this.destroyOldLightbox();
                this.loader.hide();
                this.loading = false;
                this.$notify({
                  group:'app',
                  type: 'warn',
                  title: this.$t('notifications.title.warning'),
                  text: this.$t('notifications.text.create_booking.warning_payment_cancelled'),
                });
                break;
              case MessageTypes.transactionCreated:
                await this.confirmPayment();
                this.confirmCalled = false
                this.destroyOldLightbox();
                this.loader.hide();
                this.loading = false;
                break;

              case MessageTypes.error:
                await this.failBookingTx();
                this.destroyOldLightbox();
                this.confirmCalled = false
                this.loader.hide();
                this.loading = false;
                this.$notify({
                  group:'app',
                  type:'error',
                  title: this.$t('notifications.title.error'),
                  text: this.$t('notifications.text.create_booking.error_payment_failed')
                });
                break;
            }
            next();
          },
          onReady: (hasError) => {
            if (!hasError) lightbox.show();
            else {
              this.destroyOldLightbox();
              this.$notify({
                group: 'app',
                type: 'error',
                title: this.$t('notifications.title.error'),
                text: this.$t('notifications.text.create_booking.error_payment_failed')
              });
              this.loader.hide();
              this.loading = false;
            }
          },
          onError: (error) => {
            console.error('Payment error:', error);
            this.destroyOldLightbox();
          },
        });

      } catch (err) {
        this.$_notifyError(err, this);
        if (err.data?.customerEmail) {
          this.showLoginForm = true
          this.loginForm.username = this.customerForm.email
        }
        this.destroyOldLightbox();
        this.loader.hide();
        this.loading = false;
        this.confirmCalled = false
      }
    },

    /* ============ STRIPE EMBEDDED CHECKOUT ============ */
    async startStripePayment () {
      this.loading = true
      this.destroyStripeCheckout()
      this.txnId = null
      try {
        const data = await this.$store.dispatch(
          this.payAsCustomer
            ? this.$_actionTypes.BOOKING_REQUEST_INIT_PAYMENT
            : this.$_actionTypes.BOOKING_REQUEST_INIT_PAYMENT_ANON,
          this.initPaymentPayload
        )
        localStorage.setItem(this.paymentKey, data.paymentId)
        this.persistAnonCheckoutForm()
        this.txnId = data.txId

        await loadStripeJs()
        const stripe = window.Stripe(STRIPE_PUBLISHABLE_KEY, {
          stripeAccount: this.market.stripeConnectedAccountId,
        })
        this.stripeCheckout = await stripe.createEmbeddedCheckoutPage({
          clientSecret: data.clientSecret,
          // Under if_required cards still complete in-page and fire onComplete;
          // redirect methods leave and resume via resumeRedirectedPayment.
          onComplete: () => {
            this.confirmCalled = true
            this.confirmPayment()
            this.destroyStripeCheckout()
          },
        })
        // Reveal the inline payment panel, then mount once its node exists.
        this.stripeActive = true
        await this.$nextTick()
        this.stripeCheckout.mount('#stripe-embedded-checkout')
      } catch (err) {
        this.$_notifyError(err, this)
        if (err.data?.customerEmail) {
          this.showLoginForm = true
          this.loginForm.username = this.customerForm.email
        }
        this.destroyStripeCheckout()
        this.confirmCalled = false
      } finally {
        this.loading = false
      }
    },

    async cancelStripeCheckout () {
      this.destroyStripeCheckout()
      await this.cancelBookingTx()
      this.confirmCalled = false
      this.$notify({
        group: 'app',
        type: 'warn',
        title: this.$t('notifications.title.warning'),
        text: this.$t('notifications.text.create_booking.warning_payment_cancelled'),
      })
    },

    destroyStripeCheckout () {
      if (this.stripeCheckout) {
        try {
          this.stripeCheckout.destroy()
        } catch (e) {
          // checkout already destroyed / unmounted
        }
        this.stripeCheckout = null
      }
      this.stripeActive = false
    },

    destroyOldLightbox () {
      document
        .querySelectorAll('iframe[src*="hpp.converge"]')
        .forEach(el => el.parentNode.removeChild(el));
    }
  }
}
</script>

<style scoped>
  .checkout-form-locked {
    opacity: 0.5;
    filter: blur(1.5px);
    pointer-events: none;
    user-select: none;
    transition: opacity 0.2s ease, filter 0.2s ease;
  }

  .stripe-pay-panel {
    border: 1px solid #e4e7f0;
    border-radius: 12px;
    padding: 18px;
    background: #ffffff;
    box-shadow: 0 8px 24px rgba(26, 31, 54, 0.07);
  }

  .stripe-pay-panel-head {
    display: flex;
    align-items: center;
    font-weight: 700;
    font-size: 15px;
    color: #1a1f36;
    margin-bottom: 14px;
  }

  .stripe-pay-back {
    padding-left: 0;
  }

  .stripe-reveal-enter-active {
    transition: opacity 0.25s ease, transform 0.25s ease;
  }

  .stripe-reveal-enter {
    opacity: 0;
    transform: translateY(8px);
  }

  @media (prefers-reduced-motion: reduce) {
    .stripe-reveal-enter-active {
      transition: none;
    }
  }
</style>
